import React, { useState, useEffect } from 'react';
import { Droplet, Download, FileText, Type, Image as ImageIcon } from 'lucide-react';
import { Button, Card, FileUploadZone, ProgressBar, Alert, Badge, PDFPreview, ToolActionButtons } from '../shared';
import { PDFDocument, rgb, StandardFonts, degrees } from 'pdf-lib';
import { saveAs } from 'file-saver';
import { toast } from 'react-toastify';
import '../../styles/design-system.css';

const AddWatermark = () => {
    const [file, setFile] = useState(null);
    const [isProcessing, setIsProcessing] = useState(false);
    const [progress, setProgress] = useState(0);
    const [pageCount, setPageCount] = useState(0);
    const [watermarkType, setWatermarkType] = useState('text');
    const [watermarkText, setWatermarkText] = useState('CONFIDENTIAL');
    const [opacity, setOpacity] = useState(0.3);
    const [rotation, setRotation] = useState(45);
    const [fontSize, setFontSize] = useState(48);
    const [previewBlob, setPreviewBlob] = useState(null);
    const [previewFilename, setPreviewFilename] = useState('');
    const [showPreview, setShowPreview] = useState(false);

    const handleFileSelected = async (files) => {
        if (files && files.length > 0) {
            const selectedFile = files[0];
            setFile(selectedFile);

            try {
                const arrayBuffer = await selectedFile.arrayBuffer();
                const pdf = await PDFDocument.load(arrayBuffer);
                setPageCount(pdf.getPageCount());
                toast.success(`PDF loaded: ${pdf.getPageCount()} pages`);
            } catch (error) {
                toast.error(`Failed to load PDF: ${error.message}`);
            }
        }
    };

    const addWatermark = async () => {
        if (!file) return;

        try {
            setIsProcessing(true);
            setProgress(10);

            const arrayBuffer = await file.arrayBuffer();
            const pdfDoc = await PDFDocument.load(arrayBuffer);
            const font = await pdfDoc.embedFont(StandardFonts.HelveticaBold);

            setProgress(30);

            const pages = pdfDoc.getPages();

            for (let i = 0; i < pages.length; i++) {
                const page = pages[i];
                const { width, height } = page.getSize();

                const textWidth = font.widthOfTextAtSize(watermarkText, fontSize);
                const x = (width - textWidth) / 2;
                const y = height / 2;

                page.drawText(watermarkText, {
                    x,
                    y,
                    size: fontSize,
                    font,
                    color: rgb(0.5, 0.5, 0.5),
                    opacity: opacity,
                    rotate: degrees(rotation),
                });

                setProgress(30 + ((i + 1) / pages.length) * 60);
            }

            setProgress(95);

            const pdfBytes = await pdfDoc.save();
            const blob = new Blob([pdfBytes], { type: 'application/pdf' });

            setProgress(100);

            const fileName = file.name.replace(/\.pdf$/i, '');
            const filename = `${fileName}_watermarked.pdf`;

            // Set preview
            setPreviewBlob(blob);
            setPreviewFilename(filename);

            toast.success('Watermark added! Opening preview...');

            setTimeout(() => setProgress(0), 1000);

        } catch (error) {
            console.error('Error:', error);
            toast.error(`Failed to add watermark: ${error.message}`);
        } finally {
            setIsProcessing(false);
        }
    };

    // Generate live preview when watermark settings change
    const generateLivePreview = async () => {
        if (!file) {
            setPreviewBlob(null);
            return;
        }

        try {
            const arrayBuffer = await file.arrayBuffer();
            const pdfDoc = await PDFDocument.load(arrayBuffer);
            const font = await pdfDoc.embedFont(StandardFonts.HelveticaBold);

            const pages = pdfDoc.getPages();

            for (let i = 0; i < pages.length; i++) {
                const page = pages[i];
                const { width, height } = page.getSize();

                const textWidth = font.widthOfTextAtSize(watermarkText, fontSize);
                const x = (width - textWidth) / 2;
                const y = height / 2;

                page.drawText(watermarkText, {
                    x,
                    y,
                    size: fontSize,
                    font,
                    color: rgb(0.5, 0.5, 0.5),
                    opacity: opacity,
                    rotate: degrees(rotation),
                });
            }

            const pdfBytes = await pdfDoc.save();
            const blob = new Blob([pdfBytes], { type: 'application/pdf' });
            const fileName = file.name.replace(/\.pdf$/i, '');
            const filename = `${fileName}_watermarked.pdf`;
            
            setPreviewBlob(blob);
            setPreviewFilename(filename);
        } catch (error) {
            console.error('Preview generation error:', error);
            setPreviewBlob(null);
        }
    };

    // Auto-update preview when watermark settings change
    useEffect(() => {
        if (file) {
            const timeoutId = setTimeout(() => {
                generateLivePreview();
            }, 500);
            return () => clearTimeout(timeoutId);
        }
    }, [watermarkText, opacity, rotation, fontSize, watermarkType, file]);

    return (
        <div>
            {/* Action Buttons */}
            <ToolActionButtons
                onAdd={() => {
                    document.querySelector('input[type="file"]')?.click();
                }}
                notificationCount={file ? 1 : 0}
            />

            <div className="tool-header">
                <h1 className="tool-title">
                    <Droplet size={36} style={{ display: 'inline', marginRight: 'var(--spacing-3)', color: 'var(--color-accent)' }} />
                    Add Watermark
                </h1>
                <p className="tool-description">
                    Add text or image watermarks to your PDF with customizable opacity, rotation, and positioning.
                </p>
            </div>

            {!file ? (
                <FileUploadZone
                    onFilesSelected={handleFileSelected}
                    acceptedTypes={['application/pdf']}
                    multiple={false}
                    title="Drop PDF file here"
                    description="or click to browse"
                />
            ) : (
                <Alert variant="success" title="PDF Loaded">
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-2)' }}>
                            <FileText size={16} />
                            <span>{file.name}</span>
                            {pageCount > 0 && <Badge variant="info">{pageCount} pages</Badge>}
                        </div>
                        <Button variant="ghost" size="sm" onClick={() => { setFile(null); setPageCount(0); }}>
                            Change PDF
                        </Button>
                    </div>
                </Alert>
            )}

            {file && (
                <>
                    <div style={{ marginTop: 'var(--spacing-6)' }}>
                        <Card>
                            <h3 style={{ fontSize: 'var(--text-lg)', fontWeight: 'var(--font-bold)', marginBottom: 'var(--spacing-4)' }}>
                                Watermark Settings
                            </h3>

                            <div className="grid-2" style={{ marginBottom: 'var(--spacing-4)' }}>
                                <div style={{ gridColumn: '1 / -1' }}>
                                    <label className="label">Watermark Text</label>
                                    <input
                                        type="text"
                                        value={watermarkText}
                                        onChange={(e) => setWatermarkText(e.target.value)}
                                        className="input"
                                        placeholder="Enter watermark text"
                                    />
                                </div>

                                <div>
                                    <label className="label">Opacity: {opacity.toFixed(2)}</label>
                                    <input
                                        type="range"
                                        value={opacity}
                                        onChange={(e) => setOpacity(parseFloat(e.target.value))}
                                        min="0.1"
                                        max="1"
                                        step="0.1"
                                        className="input"
                                    />
                                </div>

                                <div>
                                    <label className="label">Rotation: {rotation}°</label>
                                    <input
                                        type="range"
                                        value={rotation}
                                        onChange={(e) => setRotation(parseInt(e.target.value))}
                                        min="0"
                                        max="360"
                                        step="15"
                                        className="input"
                                    />
                                </div>

                                <div>
                                    <label className="label">Font Size</label>
                                    <input
                                        type="number"
                                        value={fontSize}
                                        onChange={(e) => setFontSize(parseInt(e.target.value) || 48)}
                                        min="12"
                                        max="120"
                                        className="input"
                                    />
                                </div>
                            </div>
                        </Card>
                    </div>

                    {isProcessing && (
                        <div style={{ marginTop: 'var(--spacing-6)' }}>
                            <ProgressBar value={progress} striped showLabel />
                        </div>
                    )}

                    <div style={{ marginTop: 'var(--spacing-6)', display: 'flex', gap: 'var(--spacing-3)' }}>
                        {previewBlob && (
                            <Button
                                variant="secondary"
                                size="lg"
                                onClick={() => setShowPreview(true)}
                                style={{ flex: 1 }}
                            >
                                <FileText size={20} style={{ marginRight: 'var(--spacing-2)' }} />
                                View Preview
                            </Button>
                        )}
                        <Button
                            variant="primary"
                            size="lg"
                            onClick={addWatermark}
                            disabled={isProcessing || !watermarkText}
                            loading={isProcessing}
                            style={{ flex: previewBlob ? 1 : 'none', width: previewBlob ? 'auto' : '100%' }}
                            fullWidth={!previewBlob}
                        >
                            <Download size={20} />
                            Add Watermark
                        </Button>
                    </div>
                </>
            )}

            {/* PDF Preview Modal */}
            {previewBlob && showPreview && (
                <PDFPreview
                    pdfBlob={previewBlob}
                    filename={previewFilename}
                    onClose={() => setShowPreview(false)}
                    onDownload={() => {
                        if (previewBlob && previewFilename) {
                            saveAs(previewBlob, previewFilename);
                            toast.success('Download started!');
                        }
                    }}
                />
            )}
        </div>
    );
};

export default AddWatermark;
